/**
 * Shared domain enums.
 *
 * These mirror the database column definitions and are used by both
 * the server (for validation, query building) and the frontend
 * (for type-safe UI state).
 */
// ─── Authentication & Authorization ────────────────────────────────────────
export var UserRole;
(function (UserRole) {
    UserRole["SUPER_ADMIN"] = "super_admin";
    UserRole["ORG_ADMIN"] = "org_admin";
    UserRole["CLINIC_ADMIN"] = "clinic_admin";
    UserRole["STAFF"] = "staff";
    UserRole["PROVIDER"] = "provider";
})(UserRole || (UserRole = {}));
export const ALL_ROLES = Object.values(UserRole);
export var UserStatus;
(function (UserStatus) {
    UserStatus["ACTIVE"] = "active";
    UserStatus["SUSPENDED"] = "suspended";
    UserStatus["DELETED"] = "deleted";
})(UserStatus || (UserStatus = {}));
export var OrganizationStatus;
(function (OrganizationStatus) {
    OrganizationStatus["ACTIVE"] = "active";
    OrganizationStatus["SUSPENDED"] = "suspended";
    OrganizationStatus["DELETED"] = "deleted";
})(OrganizationStatus || (OrganizationStatus = {}));
export var ClinicStatus;
(function (ClinicStatus) {
    ClinicStatus["ACTIVE"] = "active";
    ClinicStatus["SUSPENDED"] = "suspended";
    ClinicStatus["DELETED"] = "deleted";
})(ClinicStatus || (ClinicStatus = {}));
export var SubscriptionPlan;
(function (SubscriptionPlan) {
    SubscriptionPlan["FREE"] = "free";
    SubscriptionPlan["STARTER"] = "starter";
    SubscriptionPlan["PROFESSIONAL"] = "professional";
    SubscriptionPlan["ENTERPRISE"] = "enterprise";
})(SubscriptionPlan || (SubscriptionPlan = {}));
// ─── Permissions ───────────────────────────────────────────────────────────
export var Permission;
(function (Permission) {
    // Auth
    Permission["AUTH_LOGIN"] = "auth:login";
    Permission["AUTH_REGISTER"] = "auth:register";
    Permission["AUTH_REFRESH"] = "auth:refresh";
    // Organizations
    Permission["ORGANIZATIONS_READ"] = "organizations:read";
    Permission["ORGANIZATIONS_WRITE"] = "organizations:write";
    // Clinics
    Permission["CLINICS_READ"] = "clinics:read";
    Permission["CLINICS_WRITE"] = "clinics:write";
    // Users
    Permission["USERS_READ"] = "users:read";
    Permission["USERS_WRITE"] = "users:write";
    Permission["USERS_DELETE"] = "users:delete";
    // Patients
    Permission["PATIENTS_READ"] = "patients:read";
    Permission["PATIENTS_WRITE"] = "patients:write";
    Permission["PATIENTS_DELETE"] = "patients:delete";
    Permission["CLINICAL_HISTORY_READ"] = "clinical_history:read";
    Permission["CLINICAL_HISTORY_WRITE"] = "clinical_history:write";
    // Providers
    Permission["PROVIDERS_READ"] = "providers:read";
    Permission["PROVIDERS_WRITE"] = "providers:write";
    Permission["PROVIDERS_DEACTIVATE"] = "providers:deactivate";
    // Appointments
    Permission["APPOINTMENTS_READ"] = "appointments:read";
    Permission["APPOINTMENTS_WRITE"] = "appointments:write";
    Permission["APPOINTMENTS_CONFIRM"] = "appointments:confirm";
    Permission["APPOINTMENTS_CANCEL"] = "appointments:cancel";
    // Conversations
    Permission["CONVERSATIONS_READ"] = "conversations:read";
    Permission["CONVERSATIONS_WRITE"] = "conversations:write";
    // Knowledge base
    Permission["KNOWLEDGE_READ"] = "knowledge:read";
    Permission["KNOWLEDGE_WRITE"] = "knowledge:write";
    Permission["KNOWLEDGE_APPROVE"] = "knowledge:approve";
    // Messaging
    Permission["MESSAGING_READ"] = "messaging:read";
    Permission["MESSAGING_SEND"] = "messaging:send";
    Permission["COMMUNICATION_TEMPLATE_READ"] = "communication:template:read";
    Permission["COMMUNICATION_TEMPLATE_WRITE"] = "communication:template:write";
    Permission["COMMUNICATION_TEMPLATE_APPROVE"] = "communication:template:approve";
    Permission["COMMUNICATION_DRAFT_READ"] = "communication:draft:read";
    Permission["COMMUNICATION_DRAFT_WRITE"] = "communication:draft:write";
    Permission["COMMUNICATION_DRAFT_APPROVE"] = "communication:draft:approve";
    Permission["NOTIFICATIONS_READ"] = "notifications:read";
    Permission["NOTIFICATIONS_WRITE"] = "notifications:write";
    Permission["NOTIFICATIONS_MANAGE"] = "notifications:manage";
    // AI
    Permission["AI_CONFIGURE"] = "ai:configure";
    Permission["AI_ACTIVATE"] = "ai:activate";
    Permission["AI_DEACTIVATE"] = "ai:deactivate";
    // Audit
    Permission["AUDIT_READ"] = "audit:read";
    // Billing
    Permission["BILLING_READ"] = "billing:read";
    Permission["BILLING_WRITE"] = "billing:write";
    // Analytics
    Permission["ANALYTICS_READ"] = "analytics:read";
    // Revenue
    Permission["REVENUE_READ"] = "revenue:read";
    Permission["REVENUE_WRITE"] = "revenue:write";
})(Permission || (Permission = {}));
export const ALL_PERMISSIONS = Object.values(Permission);
// ─── Patients ──────────────────────────────────────────────────────────────
export var PatientStatus;
(function (PatientStatus) {
    PatientStatus["ACTIVE"] = "active";
    PatientStatus["INACTIVE"] = "inactive";
    PatientStatus["BLOCKED"] = "blocked";
})(PatientStatus || (PatientStatus = {}));
// ─── Appointments ──────────────────────────────────────────────────────────
export var AppointmentStatus;
(function (AppointmentStatus) {
    AppointmentStatus["PENDING"] = "pending";
    AppointmentStatus["CONFIRMED"] = "confirmed";
    AppointmentStatus["CANCELLED"] = "cancelled";
    AppointmentStatus["COMPLETED"] = "completed";
    AppointmentStatus["NO_SHOW"] = "no_show";
    AppointmentStatus["RESCHEDULED"] = "rescheduled";
})(AppointmentStatus || (AppointmentStatus = {}));
export var AppointmentSource;
(function (AppointmentSource) {
    AppointmentSource["MANUAL"] = "manual";
    AppointmentSource["AI"] = "ai";
    AppointmentSource["ONLINE"] = "online";
    AppointmentSource["PHONE"] = "phone";
})(AppointmentSource || (AppointmentSource = {}));
// ─── Conversations & Messaging ─────────────────────────────────────────────
export var MessageChannel;
(function (MessageChannel) {
    MessageChannel["WEBCHAT"] = "webchat";
    MessageChannel["WHATSAPP"] = "whatsapp";
    MessageChannel["SMS"] = "sms";
    MessageChannel["EMAIL"] = "email";
    MessageChannel["INSTAGRAM"] = "instagram";
    MessageChannel["FACEBOOK"] = "facebook";
    MessageChannel["INTERNAL"] = "internal";
})(MessageChannel || (MessageChannel = {}));
export const ALL_CHANNELS = Object.values(MessageChannel);
export var ConversationStatus;
(function (ConversationStatus) {
    ConversationStatus["ACTIVE"] = "active";
    ConversationStatus["CLOSED"] = "closed";
    ConversationStatus["ESCALATED"] = "escalated";
    ConversationStatus["AI_HANDLED"] = "ai_handled";
})(ConversationStatus || (ConversationStatus = {}));
export var MessageDirection;
(function (MessageDirection) {
    MessageDirection["INBOUND"] = "inbound";
    MessageDirection["OUTBOUND"] = "outbound";
})(MessageDirection || (MessageDirection = {}));
export var SenderType;
(function (SenderType) {
    SenderType["PATIENT"] = "patient";
    SenderType["STAFF"] = "staff";
    SenderType["AI"] = "ai";
    SenderType["SYSTEM"] = "system";
})(SenderType || (SenderType = {}));
export var MessageDeliveryStatus;
(function (MessageDeliveryStatus) {
    MessageDeliveryStatus["PENDING"] = "pending";
    MessageDeliveryStatus["SENT"] = "sent";
    MessageDeliveryStatus["DELIVERED"] = "delivered";
    MessageDeliveryStatus["READ"] = "read";
    MessageDeliveryStatus["FAILED"] = "failed";
})(MessageDeliveryStatus || (MessageDeliveryStatus = {}));
// ─── Knowledge Base ─────────────────────────────────────────────────────────
export var KnowledgeBaseStatus;
(function (KnowledgeBaseStatus) {
    KnowledgeBaseStatus["DRAFT"] = "draft";
    KnowledgeBaseStatus["PENDING_REVIEW"] = "pending_review";
    KnowledgeBaseStatus["APPROVED"] = "approved";
    KnowledgeBaseStatus["ARCHIVED"] = "archived";
})(KnowledgeBaseStatus || (KnowledgeBaseStatus = {}));
// ─── Audit ─────────────────────────────────────────────────────────────────
export var AuditResult;
(function (AuditResult) {
    AuditResult["SUCCESS"] = "success";
    AuditResult["FAILURE"] = "failure";
    AuditResult["DENIED"] = "denied";
})(AuditResult || (AuditResult = {}));
export var AuditActorType;
(function (AuditActorType) {
    AuditActorType["USER"] = "user";
    AuditActorType["SYSTEM"] = "system";
    AuditActorType["AI"] = "ai";
})(AuditActorType || (AuditActorType = {}));
// ─── General ───────────────────────────────────────────────────────────────
export const Locale = {
    EN: 'en',
    IT: 'it',
    AR: 'ar',
};
export const SUPPORTED_LOCALES = [Locale.EN, Locale.IT, Locale.AR];
export const DEFAULT_LOCALE = Locale.EN;
//# sourceMappingURL=enums.js.map